import { useCallback, useEffect, useRef } from 'react'

import {
  trackFirstMessageSent,
  trackSessionEnd,
  trackSessionStart,
} from 'tripPlanner/utils/chatAnalytics'

interface UseChatSessionTrackingArgs {
  chatId: number | undefined
  existingRouteChatId: number | undefined
}

export function useChatSessionTracking({
  chatId,
  existingRouteChatId,
}: UseChatSessionTrackingArgs) {
  const pageLoadTimestamp = useRef<number>(Date.now())
  const firstMessageFired = useRef<boolean>(false)
  const lastTrackedChatId = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (chatId !== lastTrackedChatId.current) {
      lastTrackedChatId.current = chatId
      const sessionType = existingRouteChatId !== undefined ? 'returning' : 'new'
      trackSessionStart(chatId, sessionType)
      firstMessageFired.current = false
      pageLoadTimestamp.current = Date.now()
    }
  }, [chatId, existingRouteChatId])

  useEffect(() => {
    const handlePageHide = () => {
      const durationMs = Date.now() - pageLoadTimestamp.current
      trackSessionEnd(lastTrackedChatId.current, durationMs)
    }

    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  return useCallback(() => {
    if (!firstMessageFired.current && chatId) {
      trackFirstMessageSent(chatId, Date.now() - pageLoadTimestamp.current)
      firstMessageFired.current = true
    }
  }, [chatId])
}
